import { AddComponentDataWithPositions } from 'src/generated-types/add-component'
import { ComponentBulkUpdateData } from 'src/generated-types/component-bulk-update'
import { IPositionRequestParams } from 'src/generated-types/position-request-params'
import { IROVVideosWithSharedCategories } from 'src/generated-types/rovvideo'
import { positionQueryKey } from 'src/query-keys'
import {
  BulkUpdatePositionDrawingItem,
  IBatchNumberDetails,
  ICagePositionMinimalData,
  IComponent,
  IComponentWithPositions,
  IDeviationCounts,
  IFile,
  ILocalityPositionDropDown,
  IParams,
  IPosition,
  IPositionDropDown,
  IPositionDropDownSelectOptions,
  UpdatePositionPositionDrawing,
} from '../generated-types'
import { IConfirmOlexPositions } from '../generated-types/confirm-olex'
import { IGenerateComponents, IGenerateComponentsForSelectedPositions } from '../generated-types/generate-components'
import { queryClient } from '../query-client'
import { apiConsts } from './apiConsts'
import { IPaginatedResult } from './paginated-result'
import { useDelete, useGet, usePatch, usePost, usePut } from './reactQuery'
import { requestProvider } from './requestProvider'

const baseUrl = (customerId: EntityId, localityId: EntityId, mooringId?: EntityId) =>
  mooringId === undefined
    ? `customers/${customerId}/localities/${localityId}/positions`
    : `customers/${customerId}/localities/${localityId}/moorings/${mooringId}/positions`

export const positionEndpoints = {
  getPositions(customerId: EntityId, localityId: EntityId, mooringId?: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}`
  },
  getPositionsWithMinimalData(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}/minimal-data`
  },
  getBatchNumberDetailsByProductId(customerId: EntityId, localityId: EntityId, mooringId?: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}/get-batch-number-details-by-productId`
  },
  getPositionsForDropDownByMooringId(customerId: EntityId, localityId: EntityId, mooringId?: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}/positions_for_drop_down`
  },
  getPositionsForDropDownByLocality(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/positions-for-drop-down-by-locality`
  },
  getCagesForDropDownByMooringId(customerId: EntityId, localityId: EntityId, mooringId?: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}/cages_for_drop_down`
  },
  getCagePositionsByLocality(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId, undefined)}/cages-by-locality`
  },
  getCagePositionDeviations(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId, undefined)}/get-cage-deviation-counts`
  },
  getPosition(customerId: EntityId, localityId: EntityId, mooringId: EntityId, id: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}/${id}`
  },
  getPositionROVVideos(customerId: EntityId, localityId: EntityId, mooringId: EntityId, id: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}/${id}/rov-videos`
  },
  getCageSelectOptionsByMooring(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}/cage-select-options-by-mooring`
  },
  createPosition(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}`
  },
  updatePosition(customerId: EntityId, localityId: EntityId, mooringId: EntityId, id: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}/${id}/update-position`
  },
  updatePositionBulk(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}/bulkUpdate`
  },
  uploadImage(customerId: EntityId, localityId: EntityId, id: EntityId) {
    return `${baseUrl(customerId, localityId)}/${id}/upload-image`
  },
  createComponentsForPositions(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}/generateComponentsForPositions`
  },
  generateComponentsForPositions(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}/generate-components-for-positions`
  },
  updatePositionDrawing(customerId: EntityId, localityId: EntityId, mooringId: EntityId, id: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}/${id}/update-position-drawing`
  },
  bulkUpdatePositionDrawing(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}/bulk-update-position-drawing`
  },
  deletePosition(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}`
  },
  confirmOlexPositions(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/confirm-positions`
  },
  updatePositionMinimalData(customerId: EntityId, localityId: EntityId, mooringId: EntityId, id: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}/${id}/update-position-minimal-data`
  },
  deleteROVCategory(customerId: EntityId, localityId: EntityId, mooringId?: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}`
  },
  regenerateLocalityMap(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/regenerate-map`
  },
  getComponentsforPositions(
    customerId: EntityId,
    localityId: EntityId,
    positionIds: EntityId[],
    positionDrawingId: number,
  ) {
    return `${baseUrl(customerId, localityId)}/get-components-for-positions?positionIds=${encodeURIComponent(
      positionIds.join('&'),
    )}&positionDrawingId=${encodeURIComponent(positionDrawingId)}`
  },
  getComponentsByProductId(customerId: EntityId, localityId: EntityId, positionIds: EntityId[]) {
    return `${baseUrl(customerId, localityId)}/get-components-by-productId?positionIds=${encodeURIComponent(
      positionIds.join('&'),
    )}`
  },
  addComponentForMultiplePositions(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/add-component-bulk`
  },
  updateComponentForMultiplePositions(customerId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, projectId)}/update-component-bulk`
  },
  getConnectedPositionsForDropDown(customerId: EntityId, localityId: EntityId, mooringId?: EntityId) {
    return `${baseUrl(customerId, localityId, mooringId)}/connected-positions_for_drop_down`
  },
}

export const invalidatePositionCache = {
  getPositions(
    customerId: EntityId,
    localityId: EntityId,
    mooringId?: EntityId,
    queryParams?: { isWithInfo?: boolean; isIgnoreQueryFilters?: boolean },
  ) {
    queryClient.invalidateQueries(positionQueryKey.GET_POSITIONS(customerId, localityId, mooringId, queryParams))
  },
  getPositionsWithMinimalData(customerId: EntityId, localityId: EntityId, mooringId?: EntityId) {
    queryClient.invalidateQueries(positionQueryKey.GET_POSITIONS_MINIMAL_DATA(customerId, localityId, mooringId))
  },
  getPosition(customerId: EntityId, localityId: EntityId, mooringId: EntityId, id?: EntityId, queryParams?: IParams) {
    queryClient.invalidateQueries(positionQueryKey.GET_POSITION(customerId, localityId, mooringId, id, queryParams))
  },
  getBatchNumberDetailsByProductId(
    customerId: EntityId,
    localityId: EntityId,
    mooringId?: EntityId,
    queryParams?: { productId: number },
  ) {
    queryClient.invalidateQueries(
      positionQueryKey.GET_BATCH_NUMBER_DETAILS_BY_PRODUCT_ID(
        customerId,
        localityId,
        mooringId,
        queryParams?.productId,
      ),
    )
  },
  getCagePositionDeviations(customerId: EntityId, localityId: EntityId, positionId?: EntityId) {
    queryClient.invalidateQueries(positionQueryKey.GET_Position_DEVIATION_COUNT(customerId, localityId, positionId))
  },
  getPositionROVVideos(customerId: EntityId, localityId: EntityId, mooringId: EntityId, id?: EntityId) {
    queryClient.invalidateQueries(positionQueryKey.GET_POSITION_ROVVIDEO(customerId, localityId, mooringId, id))
  },
  getPositionsForDropdownByMooring(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    queryClient.invalidateQueries(
      positionQueryKey.GET_POSITIONS_FOR_DROPDOWN_BY_MOORING(customerId, localityId, mooringId),
    )
  },
  getCagesForDropdownByMooring(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    queryClient.invalidateQueries(positionQueryKey.GET_CAGES_FOR_DROPDOWN_BY_MOORING(customerId, localityId, mooringId))
  },
  getPositionsForDropDownByMooringId(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    queryClient.invalidateQueries(
      positionEndpoints.getPositionsForDropDownByMooringId(customerId, localityId, mooringId),
    )
  },
  getPositionsForDropDownByLocality(customerId: EntityId, localityId: EntityId) {
    queryClient.invalidateQueries(positionQueryKey.GET_POSITIONS_FOR_DROPDOWN_BY_LOCALITY(customerId, localityId))
  },
  getCagesForDropDownByMooringId(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    queryClient.invalidateQueries(positionEndpoints.getCagesForDropDownByMooringId(customerId, localityId, mooringId))
  },
  getComponentsForPositions(
    customerId: EntityId,
    localityId: EntityId,
    positionIds?: EntityId[],
    positionDrawingId?: EntityId,
  ) {
    queryClient.invalidateQueries(
      positionQueryKey.GET_COMPONENTS_FOR_POSITIONS(customerId, localityId, positionIds, positionDrawingId),
    )
  },
  getConnectedPositionsForDropdown(customerId: EntityId, localityId: EntityId, mooringId?: EntityId) {
    queryClient.invalidateQueries(
      positionQueryKey.GET_CONNECTED_POSITIONS_FOR_DROPDOWN(customerId, localityId, mooringId),
    )
  },
  getComponentsByProductId(customerId: EntityId, localityId: EntityId, positionIds?: EntityId[]) {
    queryClient.invalidateQueries(positionQueryKey.GET_COMPONENTS_BY_PRODUCT_ID(customerId, localityId, positionIds))
  },
}

export const useGetPositions = (
  customerId: EntityId,
  localityId: EntityId,
  mooringId?: EntityId,
  page: number = apiConsts.Page,
  pageSize: number = apiConsts.PageSize,
  queryParams?: IPositionRequestParams,
) =>
  useGet<IPaginatedResult<IPosition>>(
    positionEndpoints.getPositions(customerId, localityId, mooringId),
    {
      page,
      pageSize,
      ...queryParams,
    },
    { queryKey: positionQueryKey.GET_POSITIONS(customerId, localityId, mooringId, queryParams) },
  )

export const useGetPositionsWithMinimalData = (
  customerId: EntityId,
  localityId: EntityId,
  mooringId: EntityId,
  queryParams?: IPositionRequestParams,
) =>
  useGet<Array<IPosition>>(
    positionEndpoints.getPositionsWithMinimalData(customerId, localityId, mooringId),
    {
      ...queryParams,
    },
    { queryKey: positionQueryKey.GET_POSITIONS_MINIMAL_DATA(customerId, localityId, mooringId, queryParams) },
  )

export const useGetBatchNumberDetailsByProductId = (
  customerId: EntityId,
  localityId: EntityId,
  mooringId?: EntityId,
  queryParams?: { productId: number },
) =>
  useGet<Array<IBatchNumberDetails>>(
    positionEndpoints.getBatchNumberDetailsByProductId(customerId, localityId, mooringId),
    {
      ...queryParams,
    },
    {
      queryKey: positionQueryKey.GET_BATCH_NUMBER_DETAILS_BY_PRODUCT_ID(
        customerId,
        localityId,
        mooringId,
        queryParams?.productId,
      ),
    },
  )

export const useGetCagePositionDeviations = (
  customerId: EntityId,
  localityId: EntityId,
  queryParams?: { positionId: EntityId | undefined },
) =>
  useGet<IDeviationCounts>(
    positionEndpoints.getCagePositionDeviations(customerId, localityId),
    { ...queryParams },
    {
      queryKey: positionQueryKey.GET_Position_DEVIATION_COUNT(customerId, localityId, queryParams?.positionId ?? 0),
      enabled: queryParams?.positionId != null,
    },
  )

export const useConfirmOlexPositions = (customerId: EntityId, localityId: EntityId) =>
  usePut<any, IConfirmOlexPositions>(positionEndpoints.confirmOlexPositions(customerId, localityId))

export const useUploadModelImage = (customerId: EntityId, localityId: EntityId, positionId: EntityId) => {
  return usePost<IFile, FormData>(positionEndpoints.uploadImage(customerId, localityId, positionId))
}

export const useGetPositionsForDropDownByMooringId = (
  customerId: EntityId,
  localityId: EntityId,
  mooringId?: EntityId,
) =>
  useGet<IPositionDropDown[]>(
    positionEndpoints.getPositionsForDropDownByMooringId(customerId, localityId, mooringId),
    undefined,
    {
      queryKey: positionQueryKey.GET_POSITIONS_FOR_DROPDOWN_BY_MOORING(customerId, localityId, mooringId),
      enabled: !!mooringId,
    },
  )

export const useGetPositionsForDropDownByLocality = (customerId: EntityId, localityId: EntityId) =>
  useGet<ILocalityPositionDropDown[]>(
    positionEndpoints.getPositionsForDropDownByLocality(customerId, localityId),
    undefined,
    {
      queryKey: positionQueryKey.GET_POSITIONS_FOR_DROPDOWN_BY_LOCALITY(customerId, localityId),
      enabled: !!customerId && !!localityId,
    },
  )

export const useGetCagesForDropDownByMooringId = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) =>
  useGet<IPosition[]>(positionEndpoints.getCagesForDropDownByMooringId(customerId, localityId, mooringId), {
    queryKey: positionQueryKey.GET_CAGES_FOR_DROPDOWN_BY_MOORING(customerId, localityId, mooringId),
  })

export const useGetCagePositionsByLocality = (customerId: EntityId, localityId: EntityId) =>
  useGet<IPosition[]>(positionEndpoints.getCagePositionsByLocality(customerId, localityId))

export const useGetPosition = (
  customerId: EntityId,
  localityId: EntityId,
  mooringId: EntityId,
  id: EntityId,
  queryParams?: IParams,
) =>
  useGet<IPosition>(positionEndpoints.getPosition(customerId, localityId, mooringId, id), queryParams, {
    queryKey: positionQueryKey.GET_POSITION(customerId, localityId, mooringId, id, queryParams),
    enabled: id != null,
  })

export const useGetCageSelectOptionsByMooring = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) =>
  useGet<Array<IPositionDropDownSelectOptions>>(
    positionEndpoints.getCageSelectOptionsByMooring(customerId, localityId, mooringId),
    {
      queryKey: positionQueryKey.GET_CAGE_SELECT_OPTION_BY_MOORING(customerId, localityId, mooringId),
    },
  )

export const useAddPosition = (customerId: EntityId, localityId: EntityId, mooringId: EntityId, formData: object) =>
  usePost<IPosition, IPosition>(positionEndpoints.createPosition(customerId, localityId, mooringId))

export const useUpdatePosition = (customerId: EntityId, localityId: EntityId, mooringId: EntityId, id: EntityId) =>
  usePut<IPosition, IPosition>(positionEndpoints.updatePosition(customerId, localityId, mooringId, id))

export const useUpdatePositionBulk = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) =>
  usePut<IPosition, IPosition[]>(positionEndpoints.updatePositionBulk(customerId, localityId, mooringId))

export const useCreateComponentsForPositions = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) =>
  usePost<IPosition, IGenerateComponents>(
    positionEndpoints.createComponentsForPositions(customerId, localityId, mooringId),
  )

export const useGenerateComponentsForPositions = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) =>
  usePost<IPosition, IGenerateComponentsForSelectedPositions>(
    positionEndpoints.generateComponentsForPositions(customerId, localityId, mooringId),
  )

export const useUpdatePositionDrawing = (
  customerId: EntityId,
  localityId: EntityId,
  mooringId: EntityId,
  id: EntityId,
) =>
  usePatch<IPosition, UpdatePositionPositionDrawing>(
    positionEndpoints.updatePositionDrawing(customerId, localityId, mooringId, id),
  )

// Returns a callable that accepts a dynamic positionId at call time,
// used for bulk unassignment where the hook URL cannot be predetermined.
export const useRemovePositionDrawings = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) => {
  return (positionId: number) =>
    requestProvider.patch(positionEndpoints.updatePositionDrawing(customerId, localityId, mooringId, positionId), {
      positionDrawingId: undefined,
      replaceComponents: true,
    })
}

export const useBulkUpdatePositionDrawing = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) =>
  usePatch<void, BulkUpdatePositionDrawingItem[]>(
    positionEndpoints.bulkUpdatePositionDrawing(customerId, localityId, mooringId),
  )

export const useDeletePosition = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) =>
  useDelete<IPosition>(positionEndpoints.deletePosition(customerId, localityId, mooringId))

export const useGetPositionROVVideo = (
  customerId: EntityId,
  localityId: EntityId,
  mooringId: EntityId,
  id: EntityId,
  queryParams?: { positionId: number },
) =>
  useGet<IROVVideosWithSharedCategories>(
    positionEndpoints.getPositionROVVideos(customerId, localityId, mooringId, id),
    queryParams,
    {
      queryKey: positionQueryKey.GET_POSITION_ROVVIDEO(customerId, localityId, mooringId, id, queryParams),
      enabled: id != null,
    },
  )

export const useUpdatePositionMinimalData = (
  customerId: EntityId,
  localityId: EntityId,
  mooringId: EntityId,
  id: EntityId,
) =>
  usePut<ICagePositionMinimalData, ICagePositionMinimalData>(
    positionEndpoints.updatePositionMinimalData(customerId, localityId, mooringId, id),
  )

export const useDeletePositionROVCategory = (customerId: EntityId, localityId: EntityId, mooringId?: EntityId) =>
  useDelete<any>(positionEndpoints.deleteROVCategory(customerId, localityId, mooringId))

export const useRegenerateLocalityMap = (customerId: EntityId, localityId: EntityId) =>
  usePost(positionEndpoints.regenerateLocalityMap(customerId, localityId))

export const useGetComponentsForPositions = (
  customerId: EntityId,
  localityId: EntityId,
  positionIds: EntityId[],
  enabled: boolean,
  positionDrawingId: number,
) => {
  return useGet<IComponentWithPositions[]>(
    positionEndpoints.getComponentsforPositions(customerId, localityId, positionIds, positionDrawingId),
    {},
    {
      enabled: positionIds.length > 0 && !!customerId && !!localityId && enabled,
      queryKey: positionQueryKey.GET_COMPONENTS_FOR_POSITIONS(customerId, localityId, positionIds, positionDrawingId),
    },
  )
}

export const useGetComponentsByProductId = (
  customerId: EntityId,
  localityId: EntityId,
  positionIds: EntityId[],
  productId: EntityId,
  positionNumber: number,
  enabled: boolean,
) => {
  return useGet<IComponent[]>(
    positionEndpoints.getComponentsByProductId(customerId, localityId, positionIds),
    { productId, positionNumber },
    {
      enabled: !!customerId && !!localityId && positionIds.length > 0 && enabled,
      queryKey: positionQueryKey.GET_COMPONENTS_BY_PRODUCT_ID(
        customerId,
        localityId,
        positionIds,
        productId,
        positionNumber,
      ),
    },
  )
}

export const useAddComponentForMultiplePositions = (customerId: EntityId, projectId: EntityId) =>
  usePost<IPosition, AddComponentDataWithPositions>(
    positionEndpoints.addComponentForMultiplePositions(customerId, projectId),
  )

export const useUpdateComponentForMultiplePositions = (customerId: EntityId, projectId: EntityId) =>
  usePost<IComponent, ComponentBulkUpdateData>(
    positionEndpoints.updateComponentForMultiplePositions(customerId, projectId),
  )

// Returns a callable that fetches all component instances for a product+drawing number
// across grouped positions, then deletes each one.
export const useDeleteComponentsByProductForPositions = (customerId: EntityId, localityId: EntityId) => {
  return async (positionIds: EntityId[], productId: EntityId, positionDrawingNumber: number) => {
    const response = await requestProvider.get(
      positionEndpoints.getComponentsByProductId(customerId, localityId, positionIds),
      { params: { productId, positionNumber: positionDrawingNumber } },
    )
    const data: IComponent[] = (response as any).data ?? []
    await Promise.all(
      data.map(c => requestProvider.delete(`customers/${customerId}/positions/${c.positionId}/components/${c.id}`)),
    )
  }
}
export const useGetConnectedPositionsForDropDown = (
  customerId: EntityId,
  localityId: EntityId,
  mooringId: EntityId,
) => {
  return useGet<IPosition[]>(
    positionEndpoints.getConnectedPositionsForDropDown(customerId, localityId, mooringId),
    {},
    {
      enabled: !!customerId && !!localityId && !!mooringId,

      queryKey: positionQueryKey.GET_CONNECTED_POSITIONS_FOR_DROPDOWN(customerId, localityId, mooringId),
    },
  )
}
